import { useState, useEffect } from 'react';
import { doc, onSnapshot, collection, query, orderBy, runTransaction, serverTimestamp, getDoc, increment } from 'firebase/firestore'; // increment EKLENDI
import { intervalToDuration, isPast } from 'date-fns';
import { db } from '../../../firebase';

const useServiceDetail = (id) => {
    const [serviceRecord, setServiceRecord] = useState(null);
    const [payments, setPayments] = useState([]);
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [kalanSure, setKalanSure] = useState('');

    // Fetch Service Record and Payments
    useEffect(() => {
        if (!id) return;

        const docRef = doc(db, "serviceRecords", id);
        const unsubscribeRecord = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setServiceRecord({ id: docSnap.id, ...docSnap.data() });
            } else {
                console.log("Servis kaydı bulunamadı!");
                setServiceRecord(null);
            }
            setLoading(false);
        });

        const paymentsRef = collection(db, "serviceRecords", id, "payments");
        const q = query(paymentsRef, orderBy("tarih", "desc"));
        const unsubscribePayments = onSnapshot(q, (snapshot) => {
            setPayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubscribeRecord();
            unsubscribePayments();
        };
    }, [id]);

    // Fetch Customer
    useEffect(() => {
        if (serviceRecord?.musteriId) {
            const fetchCustomer = async () => {
                try {
                    const docRef = doc(db, "customers", serviceRecord.musteriId);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setCustomer(docSnap.data());
                    }
                } catch (error) {
                    console.error("Müşteri getirilemedi:", error);
                }
            };
            fetchCustomer();
        }
    }, [serviceRecord?.musteriId]);

    // Countdown Timer
    useEffect(() => {
        if (serviceRecord && serviceRecord.teslimTarihi) {
            const updateCountdown = () => {
                const teslimTarihiDate = serviceRecord.teslimTarihi.toDate();
                const now = new Date();

                if (isPast(teslimTarihiDate)) {
                    setKalanSure("Teslim tarihi geçti!");
                    return;
                }

                const duration = intervalToDuration({ start: now, end: teslimTarihiDate });
                const parts = [];
                if (duration.days > 0) parts.push(`${duration.days} gün`);
                if (duration.hours > 0) parts.push(`${duration.hours} saat`);
                if (duration.minutes > 0) parts.push(`${duration.minutes} dakika`);
                setKalanSure(parts.join(' '));
            };

            updateCountdown();
            const interval = setInterval(updateCountdown, 1000 * 60);
            return () => clearInterval(interval);
        }
    }, [serviceRecord]);

    // Handlers
    const handleAddPayment = async (paymentData) => {
        const recordRef = doc(db, "serviceRecords", id);
        try {
            await runTransaction(db, async (transaction) => {
                const recordDoc = await transaction.get(recordRef);
                if (!recordDoc.exists()) { throw "Servis kaydı bulunamadı!"; }

                const currentRecordData = recordDoc.data();
                const sharedId = doc(collection(db, "transactions")).id;
                const newPaymentRef = doc(recordRef, "payments", sharedId);

                // 1. Servis Kaydına Ödeme Ekle
                transaction.set(newPaymentRef, {
                    ...paymentData,
                    tarih: serverTimestamp(),
                    musteriId: currentRecordData.musteriId,
                    musteriAdSoyad: currentRecordData.musteriAdSoyad,
                    cihazSeriNumarasi: currentRecordData.cihazSeriNumarasi,
                });

                // 2. Servis Kaydı Finansal Güncelleme
                const newTotalPaid = (currentRecordData.odenenTutar || 0) + paymentData.tutar;
                transaction.update(recordRef, {
                    odenenTutar: newTotalPaid,
                    kalanTutar: currentRecordData.toplamTutar - newTotalPaid
                });

                // 3. Müşteri Cari Hareketine Ekle (Transactions)
                const transactionRef = doc(db, "transactions", sharedId);
                transaction.set(transactionRef, {
                    customerId: currentRecordData.musteriId,
                    customerName: currentRecordData.musteriAdSoyad,
                    type: "tahsilat",
                    amount: paymentData.tutar,
                    paymentMethod: paymentData.odemeYontemi,
                    description: `Servis Ödemesi: #${serviceRecord.serviceNo || serviceRecord.cihazSeriNumarasi} - ${paymentData.aciklama || ''}`,
                    timestamp: serverTimestamp(),
                    refId: id,
                    refType: 'service' // service vs sale
                });

                // 4. Müşteri Bakiyesini Düşür
                const customerRef = doc(db, "customers", currentRecordData.musteriId);
                transaction.update(customerRef, {
                    bakiye: increment(-paymentData.tutar)
                });
            });
        } catch (e) {
            console.error("Ödeme eklenirken hata: ", e);
            alert("Ödeme eklenirken bir hata oluştu.");
        }
    };

    const handleDeletePayment = async (payment) => {
        if (!window.confirm(`${(payment.tutar || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })} tutarındaki ödemeyi silmek istediğinizden emin misiniz?`)) {
            return;
        }

        const recordRef = doc(db, "serviceRecords", id);
        const paymentRef = doc(recordRef, "payments", payment.id);
        const transactionRef = doc(db, "transactions", payment.id);

        try {
            await runTransaction(db, async (transaction) => {
                const recordDoc = await transaction.get(recordRef);
                if (!recordDoc.exists()) { throw "Servis kaydı bulunamadı!"; }

                const currentRecordData = recordDoc.data();
                transaction.delete(paymentRef);

                // Ayrıca genel Cari İşlemi de (Transaction) sil ve bakiyeyi geri al
                transaction.delete(transactionRef);

                const customerRef = doc(db, "customers", currentRecordData.musteriId);
                transaction.update(customerRef, {
                    bakiye: increment(payment.tutar)
                });

                const newTotalPaid = (currentRecordData.odenenTutar || 0) - payment.tutar;

                transaction.update(recordRef, {
                    odenenTutar: newTotalPaid,
                    kalanTutar: currentRecordData.toplamTutar - newTotalPaid
                });
            });
        } catch (e) {
            console.error("Ödeme silinirken hata: ", e);
            alert("Ödeme silinirken bir hata oluştu.");
        }
    };

    return {
        serviceRecord,
        payments,
        customer,
        loading,
        kalanSure,
        handleAddPayment,
        handleDeletePayment
    };
};

export default useServiceDetail;
